import { h } from "preact";
import { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from "preact/hooks";

import interact, { makeNewRestrictedPosition } from "../../interact";

import { play, pause, backward, xMark } from "../../icons.js";
import { fromJsonApi } from "../../models.js";
import Question from "../../models/question.js";
import { unescapeStr } from "../../utils/xml.js";
import MultipartQuestion from "../../models/multipart-question";
import MultipleChoiceQuestion from "../../models/multiple-choice-question";
import { playAudio } from "./tts-playback.js";

/**
 *
 * @typedef {Object} Options
 * @property {Object | undefined} credentials,
 * @property {() => HTMLElement} fetchReferenceMaterialHtml,
 * @property {string | undefined} outputBucket, 
 * @property {boolean} hasReferenceMaterial,
 * @property {{
 *    fetchQuestionBodyHTML: () => HTMLElement,
 *    fetchChoiceContent: () => (string|HTMLElement)[]
 *    hasChoiceContent: boolean
 *  } | undefined} externalQuestion,
 *  @property {import("../../shared-types").ItemJSON | undefined} assessmentItem
 * }}
 */

/**
 *
 * @typedef {Options & {questionId: string, close: () => void, dragContainer?: string}} Props 
 * }}
 */

/** @typedef {{url: string, marks: {type: string, value: string, time: number}[]}} SpeechSource */
/** @typedef {SpeechSource["marks"][number]} SpeechMark */
/** @typedef {{root: Element | null, roots: Element[] | null, startNode: Node | null, startNodeOffset: number | null}} Highlight */
/** @typedef {{ssml: string, highlight: Highlight, sourceType: string, revision: number}} SpeechInput */
/** @typedef {"loading" | "playing" | "paused" | "ended"} Phase */
/** @typedef {{player: HTMLAudioElement, controller: AbortController, input: SpeechInput, sources: SpeechSource[], phase: Phase, detach: () => void}} Attempt */

const finishedPlaybackEvent = new Event('finishedPlayback');

const EmptySrc = "data:audio/mpeg;base64,SUQzBAAAAAABEVRYWFgAAAAtAAADY29tbWVudABCaWdTb3VuZEJhbmsuY29tIC8gTGFTb25vdGhlcXVlLm9yZwBURU5DAAAAHQAAA1N3aXRjaCBQbHVzIMKpIE5DSCBTb2Z0d2FyZQBUSVQyAAAABgAAAzIyMzUAVFNTRQAAAA8AAANMYXZmNTcuODMuMTAwAAAAAAAAAAAAAAD/80DEAAAAA0gAAAAATEFNRTMuMTAwVVVVVVVVVVVVVUxBTUUzLjEwMFVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVf/zQsRbAAADSAAAAABVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVf/zQMSkAAADSAAAAABVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVV";

/**
 * @param {Props} props
 */
export default function (props) {
  const activeAttempt = useRef(/** @type {Attempt | null} */ (null));
  const retryInput = useRef(/** @type {SpeechInput | null} */ (null));
  const inputRevision = useRef(0);
  const [playback, setPlayback] = useState(/** @type {{phase: Phase | "idle" | "error", error: string | null, attempt: Attempt | null}} */ ({phase: "idle", error: null, attempt: null}));
  const playing = playback.phase == "playing";
  const loading = playback.phase == "loading";
  const playbackError = playback.error;
  const audioPlayer = playback.attempt?.player;
  const srcQueue = playback.attempt?.sources;
  const htmlToHighlight = playback.attempt?.input.highlight;
  let [sourceType, setSourceType] = useState(() => {
    if (documentHasSelection()) {
      return "selection"
    } else {
      return "prompt"
    }
  });

  let [playbackSpeed, setPlaybackSpeed] = useState("medium");
  let model = /** @type Question | null */ (useMemo(
    () => props.assessmentItem ? fromJsonApi(props.assessmentItem) : null,
    [props.assessmentItem]
  ));

  let [hasSelection, setHasSelection] = useState(documentHasSelection);
  let sourceTypeSelect = useRef(/** @type {HTMLSelectElement | null} */ (null));
  let getQuestionBodyHTML = useCallback(() => {
    if (props.externalQuestion) {
      return props.externalQuestion.fetchQuestionBodyHTML();
    } else {
      if (model instanceof Question) {
        let elem = document.querySelector(".question-wrapper .question-body");
        if (elem) {
          return elem;
        }
        throw "Failed to find question body";
      } else {
        throw ("Expected assessment item or external question config")
      }
    }
  }, [props.questionId, props.externalQuestion, model]);

  let getReferenceMaterialHTML = useCallback(() => {
    return props.fetchReferenceMaterialHtml();
  }, [props.questionId, props.fetchReferenceMaterialHtml]);

  let getChoiceHtml = useCallback(
    /**
     * 
     * @param {Question | undefined} focusedModel 
     */
    (focusedModel=undefined) => {
    /**
     * @type {(String | Element)[]}
     */
    let choiceContent = [];
    if (props.externalQuestion) {
      choiceContent = props.externalQuestion.fetchChoiceContent();
    } else if(focusedModel instanceof MultipleChoiceQuestion) {
      /**
       * @type {Element[]}
       */
      let choices = [];
      if(model instanceof MultipartQuestion) {
        choices = Array.from(document.querySelectorAll(`.multipart-part[data-part-id='${focusedModel.id}'] .response`)); 
      } else if(model instanceof MultipleChoiceQuestion) {
        choices = Array.from(document.querySelectorAll(`.question-wrapper[data-question-id='${model.id}'] .response`));
      }
      if(choices.length == 0) {return {ssml: "", roots: []}}
      choices.forEach((choice) => {
        let label = choice.querySelector(".label")?.textContent;
        if(!label) return;
        choiceContent.push("<mark name='highlight_ignore_start'/>Answer <say-as interpret-as='characters'>" + label + "</say-as><break/><mark name='highlight_ignore_end'/>");
        let content = choice.querySelector(".answer");
        if(!content) return;
        choiceContent.push(content);
        if(!content.querySelector("p")) {
          choiceContent.push("<break/>");
        }
      })
    }
    let ssml = "";
    /** @type Element[] */
    let roots = [];
    choiceContent.forEach((stringOrHtml) => {
      if (typeof stringOrHtml == "string") {
        ssml += stringOrHtml
      } else {
        roots.push(stringOrHtml);
        ssml += `<mark name='highlight_jump_${roots.length - 1}'/>${Question.htmlToSSML(stringOrHtml)}`;
      }
    })

    return { roots, ssml };
  }, [props.questionId, props.externalQuestion, model]);

  // Only this object may publish state or touch its audio. Disposal severs
  // ownership before aborting, so late promises/events become harmless.
  const disposeAttempt = () => {
    const attempt = activeAttempt.current;
    activeAttempt.current = null;
    if (!attempt) return;
    attempt.controller.abort();
    attempt.detach();
    attempt.player.pause();
    attempt.player.removeAttribute("src");
    attempt.player.load();
    attempt.sources.forEach((source) => URL.revokeObjectURL(source.url));
  };

  const clearPlayback = (resetSourceType = false) => {
    disposeAttempt();
    retryInput.current = null;
    setPlayback({phase: "idle", error: null, attempt: null});
    if (resetSourceType) setSourceType("prompt");
  };

  const resetPlayer = () => clearPlayback(true);

  /** @param {Attempt} attempt @param {Phase} phase */
  const publish = (attempt, phase) => {
    if (activeAttempt.current != attempt) return;
    attempt.phase = phase;
    setPlayback({phase, error: null, attempt});
  };

  /** @param {Attempt} attempt @param {unknown} error */
  const failAttempt = (attempt, error) => {
    if (activeAttempt.current != attempt) return;
    console.error("Text-to-speech playback failed", error);
    disposeAttempt();
    retryInput.current = attempt.input;
    setPlayback({phase: "error", error: "Text to speech could not play. Please try again.", attempt: null});
  };

  /** @param {Attempt} attempt */
  const startAudio = async (attempt) => {
    try {
      await playAudio(attempt.player, attempt.controller.signal);
      if (attempt.player.ended) return; // A short chunk may already have advanced.
      publish(attempt, "playing");
    } catch (error) {
      failAttempt(attempt, error);
    }
  };

  const togglePlay = async (e) => {
    e.preventDefault();
    const previous = activeAttempt.current;
    if (previous?.phase == "playing" && !previous.player.paused && !previous.player.ended && !previous.player.error) {
      previous.player.pause();
      publish(previous, "paused");
      return;
    }

    // Resume/replay only successful media for the current input. Loading or
    // failed attempts always take the fresh-attempt path below.
    if (previous && previous.phase != "loading" && !previous.player.error &&
        previous.input.revision == inputRevision.current && previous.input.sourceType == sourceType) {
      if (previous.phase == "ended") {
        previous.player.src = previous.sources[0].url;
        previous.player.dispatchEvent(finishedPlaybackEvent);
      }
      publish(previous, "loading");
      await startAudio(previous);
      return;
    }

    let input = retryInput.current?.revision == inputRevision.current ? retryInput.current : null;
    if (!input && previous?.input.revision == inputRevision.current &&
        (previous.phase == "loading" || previous.player.error)) {
      input = previous.input;
    }
    if (!input) {
      /**
       * @type {Element | null}
       */
      let html = null;

      /**
       * @type {Node | null}
       */
      let startNode = null;

      /**
       * @type {number | null}
       */
      let startNodeOffset = null;

      /**
       * @type {Element[] | null}
       */
      let roots = null;

      let ssml = "";
      try {
        if (sourceType == "prompt") {
          html = getQuestionBodyHTML();
          ssml = model ? model.promptSSML() : Question.htmlToSSML(html);
        } else if (sourceType == "reference-material") {
          html = getReferenceMaterialHTML();
          ssml = Question.htmlToSSML(html);
        } else if (sourceType == "answer-choices") {
          let choiceHtml = getChoiceHtml();
          ssml = choiceHtml.ssml;
          roots = choiceHtml.roots;
        } else if (sourceType == "selection") {
          let selection = document.getSelection();
          if (!selection || selection.rangeCount == 0) { return };

          let range = selection.getRangeAt(0);
          // A text node may be split by highlighting; keep a stable root that
          // also contains the new sibling text nodes.
          html = range.commonAncestorContainer instanceof Element
            ? range.commonAncestorContainer
            : range.commonAncestorContainer.parentElement;
          startNode = range.startContainer;
          startNodeOffset = range.startOffset;

          let contents = range.cloneContents();
          var div = document.createElement('div');
          div.appendChild(contents);

          //if the selection is for the question body we should apply additional processing
          if (range.commonAncestorContainer instanceof HTMLElement &&
            range.commonAncestorContainer.closest(".question-body") &&
            model instanceof Question) {

            model.processPromptMarkupForTts(div)
          }

          ssml = Question.htmlToSSML(div);
          selection.removeAllRanges();
        } else if (sourceType.startsWith("part_")) {
          if(!(model instanceof MultipartQuestion)) {return}
          let [_p, id, type] = sourceType.split("_");
          let part = model.parts.find((part) => {
            return String(part.id) == id
          });
          if(!part) return;
          if(type == "prompt") {
            html = document.querySelector(`.multipart-part[data-part-id='${part.id}'] .question-body`);
            ssml = part.promptSSML();
          } else if(type == "answer-choices") {
            let choiceHtml = getChoiceHtml(part);
            ssml = choiceHtml.ssml;
            roots = choiceHtml.roots;
          }
        }
      } catch (error) {
        clearPlayback();
        console.error("Text-to-speech text preparation failed", error);
        setPlayback({phase: "error", error: "Text to speech could not read this text. Please try again.", attempt: null});
        return;
      }

      if ((!html && !roots) || !ssml.trim()) return;
      input = {ssml, highlight: {root: html, roots, startNode, startNodeOffset}, sourceType, revision: inputRevision.current};
    }

    disposeAttempt();
    retryInput.current = null;
    const player = new Audio();
    const controller = new AbortController();
    /** @type {Attempt} */
    const attempt = {player, controller, input, sources: [], phase: "loading", detach() {}};
    activeAttempt.current = attempt;
    publish(attempt, "loading");
    window["_audioPlayer"] = player;

    // Unlock this attempt's element during the click (Safari permission is per
    // element). Finish its play promise before replacing the silent source.
    player.src = EmptySrc;
    const warmup = playAudio(player, controller.signal);
    const synthesize = async () => {
      const { default: TtsClient } = await import("../../../tts/index.js");
      controller.signal.throwIfAborted();
      const client = new TtsClient({credentials: props.credentials, outputBucket: props.outputBucket});
      const sources = await client.synthesizeSpeech(attempt.input.ssml, playbackSpeed, {signal: controller.signal});
      if (controller.signal.aborted) {
        sources.forEach((source) => URL.revokeObjectURL(source.url));
        controller.signal.throwIfAborted();
      }
      attempt.sources = sources;
      return sources;
    };

    try {
      const [, sources] = await Promise.all([warmup, synthesize()]);
      controller.signal.throwIfAborted();
      if (!sources.length) throw new Error("Text-to-speech returned no audio");
      attempt.sources = sources.map((source) => ({...source, marks: filterMarks(source.marks)}));

      const ended = () => {
        if (activeAttempt.current != attempt || !player.ended) return;
        const index = attempt.sources.findIndex((source) => source.url == player.src);
        if (index < 0) return;
        if (index == attempt.sources.length - 1) {
          player.dispatchEvent(finishedPlaybackEvent);
          publish(attempt, "ended");
        } else {
          player.src = attempt.sources[index + 1].url;
          publish(attempt, "loading");
          void startAudio(attempt);
        }
      };
      const failed = () => {
        if (player.error) failAttempt(attempt, new Error(player.error.message || "Audio playback failed"));
      };
      player.addEventListener("ended", ended);
      player.addEventListener("error", failed);
      attempt.detach = () => {
        player.removeEventListener("ended", ended);
        player.removeEventListener("error", failed);
      };
      player.src = attempt.sources[0].url;
      await startAudio(attempt);
    } catch (error) {
      failAttempt(attempt, error);
    }
  };

  useEffect(() => {
    let position = {x: 0, y: 0};

    interact(".tts-floating-window").draggable({
      inertia: true,
      allowFrom: ".drag-handle",
      autoScroll: {
        enabled: true
      },
      listeners: {
        move: (evt) => {
          position = makeNewRestrictedPosition(evt, position);
          if (evt.target) {
            evt.target.style.transform = `translate(${position.x}px, ${position.y}px)`
          }
        },
      }
    });

    return function cleanup() {
      resetPlayer();
    };
  }, []);

  // Cancel old playback before the new question can receive user input.
  useLayoutEffect(() => {
    resetPlayer();
  }, [props.questionId]);

  useEffect(() => {
    let callback = () => {
      let selection = document.getSelection();
      let hasSelection = selection !== null && !selection.isCollapsed;
      setHasSelection(hasSelection)
      if (hasSelection) {
        setSourceType("selection")
        inputRevision.current += 1;
        retryInput.current = null;
      } else if (!hasSelection && sourceTypeSelect.current?.value == "selection") {
        setSourceType("prompt")
      }
    };

    document.addEventListener("selectionchange", callback);
    return function cleanup() {
      document.removeEventListener("selectionchange", callback);
    }
  }, []);

  useEffect(() => {
    // Initialize once the audio is ready, so selection offsets are applied
    // only once rather than splitting the start node again after synthesis.
    if (!htmlToHighlight || !srcQueue?.length || !audioPlayer) return;
    const player = audioPlayer;

    let marker = document.createElement("span");
    marker.classList.add("tts-highlight");

    /**
     * 
     * @param {Node | undefined} currentNode 
     * @returns 
     */
    function buildWalker(currentNode = undefined) {
      if (!htmlToHighlight) return;
      let initialRoot = htmlToHighlight.root || (htmlToHighlight.roots ? htmlToHighlight.roots[0] : null);
      if (!initialRoot) return;
      let walker = document.createTreeWalker(initialRoot, NodeFilter.SHOW_TEXT + NodeFilter.SHOW_ELEMENT, {
        acceptNode(node) {
          if (node instanceof HTMLElement) {
            if (['video', 'audio', 'iframe', 'title', 'style', 'script'].includes(node.tagName.toLowerCase())) {
              return NodeFilter.FILTER_REJECT
            }

            if (node.classList.contains("drawing-editor")) {
              return NodeFilter.FILTER_REJECT
            }

            return NodeFilter.FILTER_ACCEPT
          } else {
            return NodeFilter.FILTER_ACCEPT
          };
        }
      });

      if (htmlToHighlight.startNode && initialRoot.contains(htmlToHighlight.startNode)) {
        let startNode = htmlToHighlight.startNode;
        if (htmlToHighlight.startNodeOffset && htmlToHighlight.startNodeOffset > 0) {
          if (startNode instanceof Text) {
            if (htmlToHighlight.startNodeOffset <= startNode.length) {
              walker.currentNode = startNode.splitText(htmlToHighlight.startNodeOffset);
            } else {
              walker.currentNode = startNode;
            }
          } else {
            let offsetNode = startNode.childNodes.item(htmlToHighlight.startNodeOffset);
            walker.currentNode = offsetNode || startNode;
          }
        } else {
          walker.currentNode = startNode;
        }
      }

      if (currentNode && initialRoot.contains(currentNode)) {
        walker.currentNode = currentNode;
      }

      return walker;
    }

    let maybeWalker = buildWalker();
    if (!maybeWalker) return;

    let walker = maybeWalker;

    let lastMark = null;
    let cancel = false;

    function clearMarker() {
      let maybeParent = marker.parentElement;
      if (maybeParent) {
        let parent = maybeParent;
        marker.childNodes.forEach((node) => {
          parent.insertBefore(node, marker);
        })

        parent.removeChild(marker);
      }
    }

    /**
     * 
     * @param {SpeechMark} instructionMark 
     */
    function processInstructionMark(instructionMark) {
      if (instructionMark.value.startsWith("highlight_jump") && htmlToHighlight && htmlToHighlight.roots && htmlToHighlight.roots.length > 0) {
        let jumpIndex = parseInt(instructionMark.value.split("_")[2]);
        let jumpRoot = htmlToHighlight.roots[jumpIndex];
        if (jumpRoot) {
          walker = document.createTreeWalker(jumpRoot, NodeFilter.SHOW_TEXT + NodeFilter.SHOW_ELEMENT, null);
        }
      }
    }

    let lastTime = 0;

    function renderHighlight() {
      let currentSrc = srcQueue?.find((src) => { return src.url == player.src });
      // A queued frame can run after finishedPlayback clears the marker.
      // Do not redraw the last mark once the audio has ended.
      if (cancel || !currentSrc || player.ended) {
        return;
      }

      let currentTime = (player.currentTime * 1000);
      if (currentTime <= lastTime && !player.paused) {
        requestAnimationFrame(renderHighlight);
        return;
      }

      lastTime = currentTime;

      let closestMark = null;
      /** @type SpeechMark | null*/
      let instructionMark = null;
      for (let mark of currentSrc.marks ?? []) {
        if (mark.time > currentTime) {
          if (mark.type == "ssml") {
            instructionMark = mark;
            continue;
          } else {
            break;
          }
        }

        if (mark.type != "ssml") {
          closestMark = mark;
        }
      }

      if (instructionMark) {
        if (closestMark && closestMark.time > instructionMark.time) {
          processInstructionMark(instructionMark);
          instructionMark = null
        } else if (!closestMark) {
          processInstructionMark(instructionMark);
          instructionMark = null
        }
      }

      if (closestMark) {
        if (closestMark != lastMark) {
          clearMarker()

          /** @type Node | null */
          let currentNode = walker.currentNode;
          let originalNode = currentNode;
          while (currentNode) {
            if (!currentNode) {
              break;
            }

            if (currentNode.nodeType == Node.TEXT_NODE) {
              let textNode = /** @type {Text} */ (currentNode);

              let textToFind = unescapeStr(closestMark.value);
              let idx = textNode.textContent?.indexOf(textToFind);
              if (idx !== undefined && idx >= 0) {
                let textNodeToHighlight = textNode.splitText(idx);
                let nextTextNode = textNodeToHighlight.splitText(textToFind.length);
                marker.appendChild(textNodeToHighlight);
                textNode.parentNode?.insertBefore(marker, nextTextNode);
                walker.currentNode = nextTextNode;
                lastMark = closestMark;
                break;
              }
            }

            currentNode = walker.nextNode();
            if (!currentNode) {
              lastMark = closestMark;
              let maybeWalker = buildWalker(originalNode);
              if (!maybeWalker) return;
              walker = maybeWalker;

              break
            };
          }
        }
      }

      if (instructionMark) {
        processInstructionMark(instructionMark);
      }

      if (!player.paused) {
        requestAnimationFrame(renderHighlight);
      }
    }

    let playCallback = () => {
      lastTime = 0;
      requestAnimationFrame(renderHighlight);
    };

    //When playback has finished make a new walker
    let finishedPlaybackCallback = () => {
      lastTime = 0;
      lastMark = null;
      clearMarker();
      let maybeWalker = buildWalker();
      if (!maybeWalker) return;
      walker = maybeWalker;
    };

    if (!player.paused) {
      requestAnimationFrame(renderHighlight);
    }
    player.addEventListener("play", playCallback);
    player.addEventListener("finishedPlayback", finishedPlaybackCallback);

    return () => {
      clearMarker();
      player.removeEventListener("play", playCallback);
      player.removeEventListener("finishedPlayback", finishedPlaybackCallback);
      cancel = true
    }
  }, [htmlToHighlight, srcQueue, audioPlayer]);

  return (
    <div className={playbackError ? "tts-floating-window has-error" : "tts-floating-window"}>
      <div class="drag-handle tts-floating-window-header">
        <div class="tts-floating-window-title">Text to Speech</div>
        <span class="tts-floating-window-exit" dangerouslySetInnerHTML={{ __html: xMark }} onClick={props.close}></span>
      </div>
      <div class="controls">
        <button
          type="button"
          aria-label={loading ? "Restart text to speech" : playing ? "Pause text to speech" : "Play text to speech"}
          className={loading ? "play item-player-loading" : "play"}
          dangerouslySetInnerHTML={{
            __html: loading ? "" : playing ? pause : play,
          }}
          onPointerDown={(e) => { e.preventDefault() }}
          onClick={togglePlay}
        ></button>

        <span
          className="rewind"
          dangerouslySetInnerHTML={{ __html: backward }}
          onClick={(e) => {
            e.preventDefault()
            const player = activeAttempt.current?.player;
            if (!player) return;
            player.dispatchEvent(finishedPlaybackEvent);
            player.currentTime = 0;
          }}
        ></span>
        <div className="playback-speed">
          Speed:
          <select
            value={playbackSpeed}
            onChange={(event) => {
              clearPlayback();
              setPlaybackSpeed(event.currentTarget.value);
            }}
          >
            <option value="slow">Slow</option>
            <option value="medium">Normal</option>
            <option value="fast">Fast</option>
          </select>
        </div>

        <div className="type-select">
          Play:
          <select
            value={sourceType}
            ref={sourceTypeSelect}
            onChange={(event) => {
              resetPlayer();
              let value = event.currentTarget.value;
              setSourceType(value);
              let selection = document.getSelection();
              if (selection) { selection.removeAllRanges() };
            }}
          >


            {props.questionId ? <option value="prompt">Question</option> : null}
            {(props.externalQuestion?.hasChoiceContent || model && model instanceof Question && model.hasChoiceContentForTts()) ? (
              <option value="answer-choices">Answer Choices</option>
            ) : null}
            {props.hasReferenceMaterial ? (
              <option value="reference-material">Passage</option>
            ) : null}
            {(model && model instanceof MultipartQuestion) ? (
              model.parts.map((part, index) => {
                let partId =  part.id;
                let rtn = [];
                rtn.push(<option value={`part_${partId}_prompt`}>Part {String.fromCharCode(index + 65)}</option>)
                if(part.hasChoiceContentForTts()) {
                  rtn.push(<option value={`part_${partId}_answer-choices`}>Part {String.fromCharCode(index + 65)} Answer Choices</option>)
                }
                return rtn;
              })
            ) : null}
            <option value="selection" disabled={!hasSelection}>Selected Text</option> :
          </select>
        </div>
        {playbackError ? <div className="tts-error" role="alert">{playbackError}</div> : null}
      </div>
    </div>
  );
}

function documentHasSelection() {
  let selection = document.getSelection();
  return !!(selection && !selection.isCollapsed && selection.rangeCount > 0)
}

/**
 *
 * @param {HTMLElement | undefined} container 
 */
function dragMoveListener(container) {
  /**
   * 
   * @param {import("@interactjs/types").InteractEvent} event 
   */
  return function (event) {
    var target = event.target;

    var offset = 0;
    if (container) {
      var originalScrollTop = target.getAttribute("data-scroll-top")
      if (!originalScrollTop) {
        originalScrollTop = String(container.scrollTop);
        target.setAttribute("data-scroll-top", originalScrollTop)
      }

      var currentScrollTop = container.scrollTop;
      offset = parseFloat(originalScrollTop) - currentScrollTop;
    }

    // keep the dragged position in the data-x/data-y attributes
    var x = (parseFloat(target.getAttribute("data-x") + "") || 0) + event.dx;
    var y = (parseFloat(target.getAttribute("data-y") + "") || 0) + event.dy;

    // translate the element
    target.style.transform = "translate(" + x + "px, " + (y - offset) + "px)";

    // update the posiion attributes
    target.setAttribute("data-x", String(x));
    target.setAttribute("data-y", String(y));
  };
};

/**
 * 
 * @param {SpeechMark[]} marks 
 */
function filterMarks(marks) {
  let currentlyIgnoring = false;

  return marks.filter((mark) => {
    if (mark.type == "ssml") {
      if (mark.value == "highlight_ignore_start") {
        currentlyIgnoring = true;
        return false
      } else if (mark.value == "highlight_ignore_end") {
        currentlyIgnoring = false;
        return false
      }
      return true;
    }

    return !currentlyIgnoring
  })
}
